/**
 * CinematicMode
 *
 * Scripted gameplay mode for cinematics and intros.
 * Player input is disabled; movement is controlled programmatically.
 */

import { Vector3 } from '@babylonjs/core';
import type { SplineConstraint, SplineProjection } from '../SplineConstraint';
import type { IGameplayMode } from './IGameplayMode';
import type {
  MovementInput,
  MovementResult,
  ConstraintResult,
  GameplayModeContext
} from '../../types/gameplay';

/**
 * Scripted movement command for cinematic sequences
 */
export interface ScriptedCommand {
  /** Command type */
  type: 'moveTo' | 'moveDirection' | 'jump' | 'wait' | 'face';

  /** Duration in seconds (for timed commands) */
  duration?: number;

  /** Target T value on spline (for moveTo) */
  targetT?: number;

  /** Movement direction: -1 or 1 (for moveDirection) */
  direction?: number;

  /** Facing direction: -1 or 1 (for face) */
  facing?: number;
}

export class CinematicMode implements IGameplayMode {
  readonly id = 'cinematic' as const;
  readonly displayName = 'Cinematic';
  readonly allowsInput = false;
  readonly usesVerticalInput = false;

  private commandQueue: ScriptedCommand[] = [];
  private currentCommandIndex = 0;
  private commandTimer = 0;
  private scriptedInput: { horizontal: number; vertical: number; jump: boolean } = {
    horizontal: 0,
    vertical: 0,
    jump: false
  };

  /**
   * Start a scripted movement sequence
   */
  startSequence(commands: ScriptedCommand[]): void {
    this.commandQueue = [...commands];
    this.currentCommandIndex = 0;
    this.commandTimer = 0;
    this.scriptedInput = { horizontal: 0, vertical: 0, jump: false };
  }

  /**
   * Clear the current sequence
   */
  clearSequence(): void {
    this.commandQueue = [];
    this.currentCommandIndex = 0;
    this.commandTimer = 0;
    this.scriptedInput = { horizontal: 0, vertical: 0, jump: false };
  }

  /**
   * Check if sequence is complete
   */
  isSequenceComplete(): boolean {
    return this.currentCommandIndex >= this.commandQueue.length;
  }

  updateMovement(
    _input: MovementInput, // Ignored - we use scripted input
    ctx: GameplayModeContext,
    deltaTime: number
  ): MovementResult {
    // Process scripted commands
    this.processCommands(ctx, deltaTime);

    const { path, velocity, moveSpeed } = ctx;
    const spline = path.constraint;

    // Project current position to get tangent
    const { projection } = spline.project(ctx.position, ctx.lastClosestIndex);
    const tangent = projection.tangent.normalize();

    // Calculate desired velocity from scripted input
    const desiredVelocity = new Vector3(0, velocity.y, 0);
    const inputMagnitude = Math.abs(this.scriptedInput.horizontal);

    if (inputMagnitude > 0.01) {
      const direction = Math.sign(this.scriptedInput.horizontal);
      const speed = inputMagnitude * moveSpeed;

      desiredVelocity.x = tangent.x * speed * direction;
      desiredVelocity.z = tangent.z * speed * direction;

      return {
        velocity: desiredVelocity,
        facingDirection: direction
      };
    }

    return {
      velocity: desiredVelocity,
      facingDirection: 0
    };
  }

  private processCommands(ctx: GameplayModeContext, deltaTime: number): void {
    if (this.isSequenceComplete()) {
      this.scriptedInput = { horizontal: 0, vertical: 0, jump: false };
      return;
    }

    const cmd = this.commandQueue[this.currentCommandIndex];

    switch (cmd.type) {
      case 'moveDirection':
        this.scriptedInput.horizontal = cmd.direction ?? 0;
        this.scriptedInput.jump = false;
        break;

      case 'moveTo': {
        const targetT = cmd.targetT ?? 0.5;
        const currentT = ctx.currentT;
        const diff = targetT - currentT;

        if (Math.abs(diff) < 0.01) {
          // Arrived at target T
          this.scriptedInput.horizontal = 0;
          this.advanceCommand();
          return;
        }

        this.scriptedInput.horizontal = Math.sign(diff);
        break;
      }

      case 'jump':
        this.scriptedInput.jump = true;
        // Jump is instant, advance immediately
        this.advanceCommand();
        return;

      case 'face':
        // Face command just sets facing, handled by Character
        this.advanceCommand();
        return;

      case 'wait':
        this.scriptedInput.horizontal = 0;
        this.scriptedInput.jump = false;
        break;
    }

    // Handle duration-based commands
    if (cmd.duration !== undefined) {
      this.commandTimer += deltaTime;
      if (this.commandTimer >= cmd.duration) {
        this.advanceCommand();
      }
    }
  }

  private advanceCommand(): void {
    this.currentCommandIndex++;
    this.commandTimer = 0;
    this.scriptedInput.jump = false;
  }

  constrainPosition(
    position: Vector3,
    ctx: GameplayModeContext,
    projection: SplineProjection
  ): ConstraintResult {
    // Snap XZ to spline (same as platformer)
    const constrainedPos = new Vector3(
      projection.splinePoint.x,
      position.y,
      projection.splinePoint.z
    );

    return {
      position: constrainedPos,
      closestIndex: ctx.lastClosestIndex
    };
  }

  getTargetRotation(
    movementDirection: number,
    spline: SplineConstraint,
    t: number
  ): number {
    const point = spline.getPointAt(t);
    const { projection } = spline.project(point, 0);
    const tangent = projection.tangent;

    const facingTangent = movementDirection >= 0 ? tangent : tangent.negate();
    return Math.atan2(facingTangent.x, facingTangent.z);
  }

  /**
   * Get current scripted jump state (for Character to check)
   */
  isJumpRequested(): boolean {
    return this.scriptedInput.jump;
  }
}
